import axios from 'axios';

const COINGECKO_API_BASE = 'https://api.coingecko.com/api/v3';

// Map of common symbols to CoinGecko IDs
const COIN_ID_MAP = {
  'BTC': 'bitcoin',
  'ETH': 'ethereum',
  'USDC': 'usd-coin',
  'USDT': 'tether',
  'DAI': 'dai',
  'MATIC': 'matic-network',
  'BNB': 'binancecoin',
  'SOL': 'solana',
  'ADA': 'cardano',
  'DOT': 'polkadot',
  'AVAX': 'avalanche-2',
  'LINK': 'chainlink'
};

// Demo prices for fallback when API is unavailable
const DEMO_PRICES = {
  'BTC': { price: 42850.32, change24h: 2.45, marketCap: 839000000000 },
  'ETH': { price: 2245.67, change24h: -1.23, marketCap: 270000000000 },
  'USDC': { price: 1.0, change24h: 0.01, marketCap: 25000000000 },
  'USDT': { price: 1.0, change24h: -0.02, marketCap: 95000000000 },
  'DAI': { price: 1.0, change24h: 0.0, marketCap: 5000000000 },
  'MATIC': { price: 0.89, change24h: 3.21, marketCap: 8000000000 },
  'BNB': { price: 315.42, change24h: 1.87, marketCap: 47000000000 },
  'SOL': { price: 98.76, change24h: 5.43, marketCap: 42000000000 },
  'ADA': { price: 0.52, change24h: -0.98, marketCap: 18000000000 },
  'DOT': { price: 7.23, change24h: 2.11, marketCap: 9000000000 },
  'AVAX': { price: 36.54, change24h: 4.32, marketCap: 13000000000 },
  'LINK': { price: 14.87, change24h: 1.56, marketCap: 8000000000 }
};

class PriceService {
  constructor() {
    this.priceCache = {};
    this.cacheExpiry = 30000; // 30 seconds
    this.lastFetch = {};
    this.apiAvailable = true;
    this.retryCount = 0;
    this.maxRetries = 2;
    this.useDemoMode = false;
  }

  /**
   * Get demo price for a symbol
   */
  getDemoPrice(symbol) {
    const demoData = DEMO_PRICES?.[symbol?.toUpperCase()];
    if (!demoData) return null;

    // Add slight random variation to make it feel live
    const variation = (Math.random() - 0.5) * 0.02; // ±1% variation
    return {
      price: demoData?.price * (1 + variation),
      change24h: demoData?.change24h + (Math.random() - 0.5) * 0.5,
      marketCap: demoData?.marketCap
    };
  }

  /**
   * Get current price for a single cryptocurrency
   * @param {string} symbol - Crypto symbol (e.g., 'BTC', 'ETH')
   * @param {string} vsCurrency - Target currency (default: 'usd')
   * @returns {Promise<number>} Current price
   */
  async getPrice(symbol, vsCurrency = 'usd') {
    const coinId = COIN_ID_MAP?.[symbol?.toUpperCase()];
    if (!coinId) {
      console.warn(`Unknown symbol: ${symbol}`);
      return null;
    }

    const cacheKey = `${coinId}_${vsCurrency}`;
    const now = Date.now();

    // Return cached price if still valid
    if (this.priceCache?.[cacheKey] && (now - this.lastFetch?.[cacheKey]) < this.cacheExpiry) {
      return this.priceCache?.[cacheKey];
    }

    // Use demo mode if API is unavailable
    if (this.useDemoMode) {
      const demoData = this.getDemoPrice(symbol);
      return demoData?.price || null;
    }

    try {
      const response = await axios?.get(`${COINGECKO_API_BASE}/simple/price`, {
        params: {
          ids: coinId,
          vs_currencies: vsCurrency,
          include_24hr_change: true
        },
        timeout: 5000 // 5 second timeout
      });

      const price = response?.data?.[coinId]?.[vsCurrency];
      if (price) {
        this.priceCache[cacheKey] = price;
        this.lastFetch[cacheKey] = now;
        this.apiAvailable = true;
        this.retryCount = 0;
      }

      return price;
    } catch (error) {
      console.warn(`API unavailable for ${symbol}, using demo prices:`, error?.message);
      
      // Switch to demo mode after failed attempts
      if (this.retryCount >= this.maxRetries) {
        this.useDemoMode = true;
        console.info('Switched to demo price mode due to API unavailability');
      }
      this.retryCount++;

      // Return demo price as fallback
      const demoData = this.getDemoPrice(symbol);
      return demoData?.price || this.priceCache?.[cacheKey] || null;
    }
  }

  /**
   * Get prices for multiple cryptocurrencies
   * @param {string[]} symbols - Array of crypto symbols
   * @param {string} vsCurrency - Target currency (default: 'usd')
   * @returns {Promise<Object>} Object with symbol as key and price data as value
   */
  async getPrices(symbols, vsCurrency = 'usd') {
    const coinIds = symbols
      ?.map(symbol => COIN_ID_MAP?.[symbol?.toUpperCase()])
      ?.filter(Boolean);

    if (!coinIds?.length) {
      return {};
    }

    // Use demo mode if API is unavailable
    if (this.useDemoMode) {
      const result = {};
      symbols?.forEach(symbol => {
        const demoData = this.getDemoPrice(symbol);
        if (demoData) {
          result[symbol?.toUpperCase()] = demoData;
        }
      });
      return result;
    }

    try {
      const response = await axios?.get(`${COINGECKO_API_BASE}/simple/price`, {
        params: {
          ids: coinIds?.join(','),
          vs_currencies: vsCurrency,
          include_24hr_change: true,
          include_market_cap: true
        },
        timeout: 5000 // 5 second timeout
      });

      const result = {};
      symbols?.forEach(symbol => {
        const coinId = COIN_ID_MAP?.[symbol?.toUpperCase()];
        if (coinId && response?.data?.[coinId]) {
          result[symbol?.toUpperCase()] = {
            price: response?.data?.[coinId]?.[vsCurrency],
            change24h: response?.data?.[coinId]?.[`${vsCurrency}_24h_change`] || 0,
            marketCap: response?.data?.[coinId]?.[`${vsCurrency}_market_cap`] || 0
          };
        }
      });

      // Cache successful results
      if (Object.keys(result)?.length > 0) {
        this.apiAvailable = true;
        this.retryCount = 0;
      }

      return result;
    } catch (error) {
      console.warn('API unavailable for multiple prices, using demo prices:', error?.message);
      
      // Switch to demo mode after failed attempts
      if (this.retryCount >= this.maxRetries) {
        this.useDemoMode = true;
        console.info('Switched to demo price mode due to API unavailability');
      }
      this.retryCount++;

      // Return demo prices as fallback
      const result = {};
      symbols?.forEach(symbol => {
        const demoData = this.getDemoPrice(symbol);
        if (demoData) {
          result[symbol?.toUpperCase()] = demoData;
        }
      });
      return result;
    }
  }

  /**
   * Calculate exchange rate between two cryptocurrencies
   * @param {string} fromSymbol - Source crypto symbol
   * @param {string} toSymbol - Target crypto symbol
   * @returns {Promise<number>} Exchange rate
   */
  async getExchangeRate(fromSymbol, toSymbol) {
    const [fromPrice, toPrice] = await Promise.all([
      this.getPrice(fromSymbol, 'usd'),
      this.getPrice(toSymbol, 'usd')
    ]);

    if (!fromPrice || !toPrice) {
      return null;
    }

    return fromPrice / toPrice;
  }

  /**
   * Get detailed price data with 24h change
   * @param {string} symbol - Crypto symbol
   * @returns {Promise<Object>} Price data with change percentage
   */
  async getPriceWithChange(symbol) {
    const coinId = COIN_ID_MAP?.[symbol?.toUpperCase()];
    if (!coinId) {
      return null;
    }

    try {
      const response = await axios?.get(`${COINGECKO_API_BASE}/simple/price`, {
        params: {
          ids: coinId,
          vs_currencies: 'usd',
          include_24hr_change: true,
          include_market_cap: true,
          include_24hr_vol: true
        },
        timeout: 5000
      });

      const data = response?.data?.[coinId];
      if (!data) {
        return this.getDemoPrice(symbol);
      }

      return {
        price: data?.usd,
        change24h: data?.usd_24h_change,
        marketCap: data?.usd_market_cap,
        volume24h: data?.usd_24h_vol
      };
    } catch (error) {
      console.warn(`Network error fetching price for ${symbol}, using demo data:`, error?.message);
      return this.getDemoPrice(symbol);
    }
  }

  /**
   * Calculate USD value for a given amount of cryptocurrency
   * @param {string} symbol - Crypto symbol
   * @param {number} amount - Amount of crypto
   * @returns {Promise<number>} USD value
   */
  async calculateUSDValue(symbol, amount) {
    const price = await this.getPrice(symbol, 'usd');
    if (!price) {
      return 0;
    }
    return price * amount;
  }

  /**
   * Clear price cache
   */
  clearCache() {
    this.priceCache = {};
    this.lastFetch = {};
  }

  /**
   * Add custom coin mapping
   * @param {string} symbol - Crypto symbol
   * @param {string} coinGeckoId - CoinGecko coin ID
   */
  addCoinMapping(symbol, coinGeckoId) {
    COIN_ID_MAP[symbol?.toUpperCase()] = coinGeckoId;
  }
}

export default new PriceService();